import * as animateScrollTo from 'animated-scroll-to';
import * as Immutable from 'immutable';
import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Dispatch } from 'redux';
import { Loader } from 'semantic-ui-react';
import { v1 } from 'uuid';
import type { ItemParams } from 'react-contexify';
import type { History } from 'history';

import { ErrorBoundary } from '..';
import {
  resetProperties,
  setAlphaPropertiesData,
  setAlphaPropertiesError,
  toggleFunctionsProperty,
} from '../../actions/AlphasPropertiesAction';
import {
  changeTabPosition,
  changeTabPositionChild,
  changeToSimulation,
  changingTabPosition,
  cloneAlpha,
  cloneAlphaFromContent,
  closeChildTab,
  closeTab,
  createNewChildTab,
  createNewMultiTab,
  createNewSuperAlphaTab,
  createNewTab,
  fetchSimulationOptions,
  getExample,
  renameTab,
  setActiveChildTab,
  setTabCode,
  stopChangingTabPosition,
  toggleVisualization,
} from '../../actions/EditorActions/EditorActions';
import {
  addActiveContexts,
  addFlashMessages,
  changeActiveContexts,
  clearAllFlashContexts,
  onlyGenericFlashMessages,
  removeFlashContexts,
} from '../../actions/FlashMessagesActions';
import {
  changeSimulationStatus,
  simulationCheck,
  submitAlphaSimulation,
  updateAlphaPropertiesSimulation,
} from '../../actions/Simulation/DataAction';
import { cancelSimulation, fetchSimulationPost } from '../../actions/Simulation/ProgressAction';
import {
  resetFilters,
  setDefaultFilters,
  toggleSimulationFilterMobileThunk,
  updateFiltersForSingleFilterThunk,
} from '../../actions/Simulation/SimulationFiltersActions';
import { EditorMain } from '../../components/Editor/';
import { consumeClonePayload, cleanupExpiredClonePayloads } from '../../shared/helpers/clone-alpha';
import { CloneAlphaData } from '../../actions/EditorActions.types';
import { StateType } from '../../reducers';
import { alphasFetchingDataSelector } from '../../reducers/AlphasReducer/AlphasSelector';
import {
  getHasUpdatedPropertiesSelector,
  shouldCheckSelector,
  shouldSubmitSelector,
} from '../../reducers/AlphaPropertiesReducer/AlphaPropertiesSelector';
import {
  getUserIdSelector,
  getUserPermissionsSelector,
  getUserSettingsSelector,
} from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { getSettingsToMultiSimulate, getSettingsToSimulate } from '../../reducers/EditorReducer/EditorHelper';
import { EditorTab as EditorTabType } from '../../reducers/EditorReducer/EditorReducer.types';
import {
  activeTabIndexSelector,
  activeTabSelector,
  childTabsSelector,
  currentChildActiveTabLessOne,
  editorFetchingSettingsSelector,
  editorOptionsSelector,
  getExampleArrayIdsSelector,
  getInvalidIdsSelector,
  getTabsSelector,
  maxTruncationSelector,
  minTruncationSelector,
} from '../../reducers/EditorReducer/EditorSelector';
import { tabsStatusSelector } from '../../reducers/GenericSelectors';
import {
  alphaDetailIdSimulationSelector,
  alphaDetailSimulationSelector,
  dataFetchingCheckSelector,
  dataSelector,
  fetchingSelectionCodeSelector,
  isFetchingPropertiesSimulationSelector,
} from '../../reducers/SimulationReducer/DataReducer/DataSelector';
import { filtersSelector } from '../../reducers/SimulationReducer/FiltersReducer/FiltersSelector';
import {
  fetchingPercentageSelector,
  fetchingPostSelector,
  progressSelector,
} from '../../reducers/SimulationReducer/ProgressReducer/ProgressSelector';
import { checkSettings, isSimulationEdited, prefixEditTabMsg } from '../../shared/helpers';
import { isdeviceUpToTablet } from '../../shared/helpers/responsive';
import { validateFields } from '../../shared/helpers/AlphaPropertiesValidation';
import { DispatchConnect } from '../../types/';

import analytics from '../../shared/services/analytics/analytics';
import * as Sentry from '@sentry/react';
import { setActiveEditorTab } from '../../actions/SetActiveTabAction';
import { queryClient } from '../../shared/api/apiHooks';
import { Setting } from 'src/components/Editor/EditorTopBar/EditorSettingsForm';

interface EditorStateProps {
  tabs: Immutable.List<any>;
  currentActiveTab: number;
  currentMultiTabs: Immutable.List<any>;
  currentChildActiveTab: number;
  options: any;
  userId: string;
  permissions: Immutable.List<string>;
  fetchingOptions: boolean;
  isFetching: boolean;
  isFetchingCheck: boolean;
  filters: Immutable.List<any>;
  fetchingPercentage: boolean;
  fetchingPost: boolean;
  fetchingSelectionCode: boolean;
  data: any;
  userSettings: any;
  progress: any;
  isMultiTab: boolean;
  isFetchingProperties: boolean;
  alphaId: string;
  maxTruncation: number;
  minTruncation: number;
  activeTab: any;
  simulationAlphas: any;
  invalidIds: any;
  exampleArrayIds: any;
  hasUpdatedProperties: boolean;
  shouldCheck: boolean;
  shouldSubmit: boolean;
  tabsStatus: Immutable.List<any>;
}
interface EditorProps {}

interface EditorState {
  canRename: boolean;
  renameInfo: any;
  showCloseTabModal: boolean;
  showPropertiesTabModal: any;
  showChangeCodeModal: boolean;
  closeTabIndex: number | undefined;
  closeChildTabIndex: number | undefined;
  isMobile: boolean;
}

interface EditorStateDispatchers {
  dispatchCreateNewTab: any;
  dispatchCreateNewSuperAlphaTab: any;
  dispatchCreateNewMultiTab: any;
  dispatchCreateNewChildTab: any;
  dispatchSetActiveTab: any;
  dispatchSetChildTabAsActive: any;
  dispatchCloseTab: any;
  dispatchCloseChildTab: any;
  dispatchChangeToSimulation: (activeTabindex: number, childActiveTab: number) => void;
  dispatchResetProperties: () => void;
  dispatchUpdateTabCode: (newValue: any, codeType: string, currentTabIndex: number, childActiveTab: number) => void;
  dispatchGetExample: (currentTabIndex: number, childActiveTab: number) => void;
  dispatchSimulationOptions: () => void;
  dispatchSimulationPost: (params: DispatchSimulationPostParams) => void;
  dispatchFlashMessageError: (message: Immutable.List<string>) => void;
  dispatchCancelSimulation: (simulationId: string, id: string) => void;
  dispatchCloneAlphaInNewTab: (
    activeTab: number,
    childActiveTab: number | null,
    idParent: any,
    idChild: any[],
    multi?: boolean,
    type?: string
  ) => void;
  dispatchCloneAlphaFromContent: (alphaDetail: CloneAlphaData) => void;
  dispatchSimulationCheck: (alphaId: string, idParent: string, childId: string | null) => void;
  dispatchSaveAlphaProperties: (alphaId: string, data: any, idParent: string, indexChild: any) => void;
  dispatchSubmitAlpha: (id: string, idParent: string, childId: string | null, isVisualization: boolean) => void;
  dispatchRenameTab: (tabIndex: number, indexChild: any, newName: string) => void;
  dispatchTogglevisualization: (tabIndex: number) => void;
  dispatchClearAllFlashContexts: () => void;
  dispatchUpdateFiltersForSingleFilter: (historyReplace: (path: string) => void) => void;
  dispatchOnlyGenericFlashMessages: () => void;
  dispatchAddActiveContexts: (contexts: string[]) => void;
  dispatchChangeActiveContexts: (contexts: string[]) => void;
  dispatchRemoveFlashContexts: (path: string[], contexts: string[]) => void;
  dispatchToggleSimulationFilterMobile: (tabIndex: number, codeType: string, historyReplace: (path: string) => void) => void;
  dispatchToggleFunctionsProperty: (property: string, value: boolean) => void;
  dispatchSetAlphaPropertiesData: (data: any) => void;
  dispatchSetAlphaPropertiesError: (hasError: boolean) => void;
  dispatchChangeSimulationStatus: (tabIndex: number, currentStatus: string, childIndex?: number, nextStatus?: string) => void;
  dispatchChangeTabPosition: (source: number, destination: number) => void;
  dispatchChangeTabPositionChild: (currentActiveTab: number, source: number, destination: number) => void;
  dispatchResetFilters: () => void;
  dispatchSetDefaultFilters: (filters: Object, tabIndex: number) => void;
  dispatchChangingTabPosition: () => void;
  dispatchStopChangingTabPosition: () => void;
}

type EditorPropsGeneric = EditorStateProps & EditorProps & EditorStateDispatchers & RouteComponentProps<any>;

class Editor extends React.Component<EditorPropsGeneric, EditorState> {
  constructor(props: EditorPropsGeneric) {
    super(props);

    this.state = {
      canRename: false,
      renameInfo: null,
      showCloseTabModal: false,
      showPropertiesTabModal: false,
      showChangeCodeModal: false,
      closeTabIndex: undefined,
      closeChildTabIndex: undefined,
      isMobile: isdeviceUpToTablet(),
    };
  }

  componentDidMount() {
    this.props.dispatchSimulationOptions();
    window.addEventListener('resize', this.updateMobileState);
    if (this.props.invalidIds.size > 0) {
      window.addEventListener('beforeunload', this.handlerCloseTabEvent);
    } else {
      window.removeEventListener('beforeunload', this.handlerCloseTabEvent);
    }
    if (this.props.options && this.props.userSettings && this.props.currentActiveTab === -1) {
      this.handleCreateFirstTab(this.props.userSettings);
    }
    this.props.dispatchAddActiveContexts(['editor']);
    if (this.props.currentActiveTab !== undefined && this.props.currentActiveTab !== -1) {
      this.setFlashMessageContext(this.props);
    }
    if (this.state.isMobile) {
      this.props.dispatchUpdateFiltersForSingleFilter(this.props.history.replace);
    }
    this.consumeCloneFromUrl();
  }

  consumeCloneFromUrl = () => {
    const params = new URLSearchParams(this.props.location.search);
    const cloneId = params.get('cloneId');

    if (cloneId) {
      const payload = consumeClonePayload(cloneId);
      if (payload) {
        // Clean up any other expired payloads
        cleanupExpiredClonePayloads();

        // Build alpha object in the format expected by CLONE_ALPHA_FROM_CONTENT reducer
        // The reducer reads: alphaDetail.get('type'), alphaDetail.getIn(['regular', 'code']) || alphaDetail.get('regular'),
        // alphaDetail.getIn(['selection', 'code']), alphaDetail.getIn(['combo', 'code']), alphaDetail.get('settings')
        const alphaForClone = {
          regular: payload.superAlpha ? undefined : payload.code || '',
          combo: payload.combo ? { code: payload.combo } : undefined,
          selection: payload.selection ? { code: payload.selection } : undefined,
          settings: {
            ...payload.settings,
            visualization: payload.visualization,
          },
          type: payload.type || (payload.superAlpha ? 'SUPER' : 'REGULAR'),
          color: payload.color,
          name: payload.name,
          tags: payload.tags,
          category: payload.category,
        };

        this.props.dispatchCloneAlphaFromContent(alphaForClone);
      }

      // Clean URL
      this.props.history.replace('/simulate');
    }
  };

  updateMobileState = () => {
    this.setState({ isMobile: isdeviceUpToTablet() });
  };

  componentWillUnmount() {
    this.props.dispatchClearAllFlashContexts();
    this.props.dispatchOnlyGenericFlashMessages();
    window.removeEventListener('resize', this.updateMobileState);
    this.setFlashMessageContext(this.props);
  }

  componentDidUpdate(prevProps: EditorPropsGeneric, prevState: EditorState) {
    if (!prevProps.options && this.props.options && this.props.userSettings && this.props.tabs.size === 0) {
      this.handleCreateFirstTab(this.props.userSettings);
    }

    if (!prevState.isMobile && this.state.isMobile) {
      this.props.dispatchUpdateFiltersForSingleFilter(this.props.history.replace);
    }

    if (prevProps.isFetchingProperties && !this.props.isFetchingProperties && this.props.shouldCheck) {
      this.handleCheckSubmission();
    }

    if (prevProps.isFetchingProperties && !this.props.isFetchingProperties && this.props.shouldSubmit) {
      this.handleSubmitAlpha();
    }

    if (prevProps.tabs && this.props.tabs && prevProps.tabs && this.props.tabs.count !== prevProps.tabs.count) {
      this.filterValitation();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: EditorPropsGeneric) {
    if (this.props.invalidIds.size !== nextProps.invalidIds.size) {
      if (nextProps.invalidIds.size > 0) {
        window.addEventListener('beforeunload', this.handlerCloseTabEvent);
      } else {
        window.removeEventListener('beforeunload', this.handlerCloseTabEvent);
      }
    }

    if (
      nextProps.currentActiveTab !== this.props.currentActiveTab ||
      nextProps.currentChildActiveTab !== this.props.currentChildActiveTab
    ) {
      this.setFlashMessageContext(nextProps);
    }
  }

  filterValitation() {
    const { tabs, data, filters, dispatchSetDefaultFilters, dispatchResetFilters } = this.props;
    if (tabs.size !== filters.size) {
      dispatchResetFilters();
    }
    tabs.forEach((_: any, index: number) => {
      const filter = filters.get(index);

      const codeType = data && data.get([index, 'superAlpha']) ? 'superCode' : 'code';

      if (!filter) {
        dispatchSetDefaultFilters(
          {
            [codeType]: true,
            results: isdeviceUpToTablet() ? false : true,
            learn: false,
            dataExplorer: false,
            tutorial: false,
          },
          index
        );
      }
    });
  }

  setFlashMessageContext(props: EditorPropsGeneric) {
    const tabID = props.tabs.getIn([props.currentActiveTab, 'id']);
    let contexts = ['generic', `${prefixEditTabMsg}${tabID}`];

    if (props.currentMultiTabs.size !== 0) {
      const childTabID = props.currentMultiTabs.getIn([props.currentChildActiveTab, 'id']);
      contexts = contexts.concat(`${prefixEditTabMsg}${childTabID}`);
    }
    props.dispatchChangeActiveContexts(contexts);
  }

  handlerCloseTabEvent = (e: any) => {
    const confirmationMessage = 'o/';
    e.returnValue = confirmationMessage;

    return confirmationMessage;
  };

  handleCreateFirstTab = (userSettings: any) => {
    // TODO: open learn section when we create first tab
    const settings = { ...userSettings };

    this.props.dispatchCreateNewTab(settings);
  };

  handleCreateNewTab = () => {
    if (this.props.hasUpdatedProperties === true && this.state.showPropertiesTabModal === false) {
      this.setState({ showPropertiesTabModal: () => this.handleCreateNewTab() });
    } else {
      this.props.dispatchCreateNewTab(this.props.userSettings);
      if (this.props.location.pathname !== '/simulate') {
        this.props.history.push('/simulate');
      }
    }
  };

  handleNewSuperAlpha = () => {
    if (this.props.hasUpdatedProperties === true && this.state.showPropertiesTabModal === false) {
      this.setState({ showPropertiesTabModal: () => this.handleCreateNewTab() });
    } else {
      this.props.dispatchCreateNewSuperAlphaTab(this.props.userSettings);
      if (this.props.location.pathname !== '/simulate') {
        this.props.history.push('/simulate');
      }
    }
  };

  handleShowPropertiesTabModalState = (state: any) => {
    this.setState({ showPropertiesTabModal: state });
  };

  handleCreateNewMultiTab = () => {
    if (this.props.hasUpdatedProperties === true && this.state.showPropertiesTabModal === false) {
      this.setState({ showPropertiesTabModal: () => this.handleCreateNewMultiTab() });
    } else {
      this.props.dispatchCreateNewMultiTab(this.props.userSettings);
      if (this.props.location.pathname !== '/simulate') {
        this.props.history.push('/simulate');
      }
    }
  };

  handleCreateNewChildTab = () => {
    const progressTab = this.props.progress.get(this.props.currentActiveTab);
    if (progressTab.get('fetchingPercentage') || progressTab.get('fetchingPost') || progressTab.get('fetchingCancel')) {
      return;
    }
    if (this.props.hasUpdatedProperties && this.state.showPropertiesTabModal === false) {
      this.setState({ showPropertiesTabModal: () => this.handleCreateNewChildTab() });
    } else {
      const newTabSettingsValues = this.getNewChildTabSettings();
      this.props.dispatchCreateNewChildTab(this.props.currentActiveTab, newTabSettingsValues);
      if (this.props.location.pathname !== '/simulate') {
        this.props.history.push('/simulate');
      }
    }
  };

  getNewChildTabSettings() {
    const { childTabs, settings } = this.props.activeTab.toJS();
    const newTabSettings = { ...settings, ...childTabs[0].settings } as Record<string, Setting>;
    const newTabSettingsValues: Record<string, any> = {};
    for (const [key, value] of Object.entries(newTabSettings)) {
      newTabSettingsValues[key] = value.value;
    }
    return newTabSettingsValues;
  }

  onCodeChange = (newValue: any, codeType: string) => {
    const currentActiveTab = this.props.currentActiveTab;
    const childActiveTab = this.props.isMultiTab ? this.props.currentChildActiveTab : -1;
    const currentTab = this.props.tabs.get(currentActiveTab);
    const tabStatus =
      childActiveTab > -1
        ? this.props.tabsStatus.getIn([currentActiveTab, 'childTabs', childActiveTab, 'currentStatus'])
        : this.props.tabsStatus.getIn([currentActiveTab, 'parentTab', 'currentStatus']);
    const currentCode =
      codeType === 'code'
        ? newValue
        : codeType === 'combo'
        ? { combo: newValue, selection: currentTab.get('selection') }
        : { combo: currentTab.get('combo'), selection: newValue };
    const currentSettings = currentTab.get('settings');
    if (isSimulationEdited(currentCode, currentSettings.toJS(), this.props.data)) {
      // @ts-expect-error thunk dispatch argument count mismatch
      this.props.dispatchChangeSimulationStatus(currentActiveTab, tabStatus, childActiveTab);
    } else {
      const tabStatusAfterSimulting =
        childActiveTab > -1
          ? this.props.tabsStatus.getIn([currentActiveTab, 'childTabs', childActiveTab, 'statusAfterSimulation'])
          : this.props.tabsStatus.getIn([currentActiveTab, 'parentTab', 'statusAfterSimulation']);
      // @ts-expect-error thunk dispatch argument count mismatch
      this.props.dispatchChangeSimulationStatus(currentActiveTab, tabStatus, childActiveTab, tabStatusAfterSimulting);
    }
    this.props.dispatchUpdateTabCode(newValue, codeType, currentActiveTab, childActiveTab);
  };

  handleSetTabAsActive = (activateTabIndex: number) => {
    if (activateTabIndex === this.props.currentActiveTab) {
      return;
    }

    if (this.props.hasUpdatedProperties && !this.state.showPropertiesTabModal) {
      this.setState({ showPropertiesTabModal: () => this.handleSetTabAsActive(activateTabIndex) });
    } else {
      this.props.dispatchSetActiveTab(activateTabIndex, this.props.history);
    }
  };

  handleSetChildTabAsActive = (activateTabIndex: number, parentTabIndex: number = this.props.currentActiveTab) => {
    if (parentTabIndex !== this.props.currentActiveTab) {
      this.handleSetTabAsActive(parentTabIndex);
    }
    if (
      this.props.hasUpdatedProperties === true &&
      this.state.showPropertiesTabModal === false &&
      activateTabIndex !== this.props.currentChildActiveTab
    ) {
      this.setState({ showPropertiesTabModal: () => this.handleSetChildTabAsActive(activateTabIndex, parentTabIndex) });
    } else {
      this.props.dispatchSetChildTabAsActive(activateTabIndex, parentTabIndex);
    }
  };

  checkIfTabIsEmptyCode = (tabIndex: number, tabIndexChild: number | undefined = undefined) => {
    if (tabIndexChild !== undefined) {
      const childTabActive = this.props.tabs.getIn([tabIndex, 'childTabs', tabIndexChild]);

      return (
        // @ts-expect-error ImmutableJS get returns unknown
        childTabActive.get('code') === undefined ||
        // @ts-expect-error ImmutableJS get returns unknown
        childTabActive.get('code').trim().length === 0 ||
        this.props.simulationAlphas.getIn([tabIndex, 'children', tabIndexChild, 'alphaDetail', 'code']) ===
          this.props.tabs.getIn([tabIndex, 'childTabs', tabIndexChild, 'code'])
      );
    } else {
      const tabActive = this.props.tabs.get(tabIndex);
      if (tabActive === undefined) {
        return true;
      }
      const childTabs = tabActive.get('childTabs');
      if (tabActive.has('childTabs')) {
        return (
          !childTabs.find((tab: any) => tab.get('code') !== undefined && tab.get('code').trim().length > 0) ||
          !childTabs.find(
            (tab: any, indexChild: number) =>
              tab.get('code') !== this.props.simulationAlphas.getIn([tabIndex, 'children', indexChild, 'alphaDetail', 'code'])
          )
        );
      } else if (tabActive.get('superAlpha')) {
        return (
          ((tabActive && tabActive.get('combo') === undefined) ||
            (tabActive && typeof tabActive.get('combo') === 'string' && tabActive.get('combo').trim().length === 0) ||
            this.props.simulationAlphas.getIn([tabIndex, 'alphaDetail', 'combo']) === this.props.tabs.getIn([tabIndex, 'combo'])) &&
          ((tabActive && tabActive.get('selection') === undefined) ||
            (tabActive && typeof tabActive.get('selection') === 'string' && tabActive.get('selection').trim().length === 0) ||
            this.props.simulationAlphas.getIn([tabIndex, 'alphaDetail', 'selection']) === this.props.tabs.getIn([tabIndex, 'selection']))
        );
      } else {
        return (
          (tabActive && tabActive.get('code') === undefined) ||
          (tabActive && typeof tabActive.get('code') === 'string' && tabActive.get('code').trim().length === 0) ||
          this.props.simulationAlphas.getIn([tabIndex, 'alphaDetail', 'code']) === this.props.tabs.getIn([tabIndex, 'code'])
        );
      }
    }
  };

  handleCloseChild = (closeChildTabIndex: number, parentTabIndex: any) => {
    if (
      this.props.hasUpdatedProperties === true &&
      this.state.showPropertiesTabModal === false &&
      closeChildTabIndex === this.props.currentChildActiveTab
    ) {
      this.setState({ showPropertiesTabModal: () => this.handleCloseChild(closeChildTabIndex, parentTabIndex) });
    } else {
      const progressTab = this.props.progress.get(parentTabIndex);
      if (progressTab.get('fetchingPercentage') || progressTab.get('fetchingPost') || progressTab.get('fetchingCancel')) {
        this.handlerCancelSimulation();
      }
      // @ts-expect-error ImmutableJS getIn returns unknown
      if (this.props.tabs.getIn([parentTabIndex, 'childTabs']).size === 2) {
        this.props.dispatchChangeToSimulation(parentTabIndex, closeChildTabIndex);
      } else {
        this.props.dispatchCloseChildTab(parentTabIndex, closeChildTabIndex);
      }
      const parentTab = this.props.tabs.getIn([parentTabIndex, 'childTabs']);

      if (parentTab) {
        const childTab = this.props.tabs.getIn([parentTabIndex, 'childTabs', closeChildTabIndex]);

        if (childTab) {
          // @ts-expect-error dispatch thunk argument type mismatch
          this.props.dispatchRemoveFlashContexts(['editor-code'], [`${prefixEditTabMsg}${childTab.get('id')}`]);
        }
      }
    }
  };

  handleCloseParent = (closeTabIndex: number | undefined) => {
    if (this.props.hasUpdatedProperties === true && this.state.showPropertiesTabModal === false) {
      this.setState({ showPropertiesTabModal: () => this.handleCloseParent(closeTabIndex) });
    } else {
      if (this.props.fetchingPercentage) {
        this.handlerCancelSimulationWithIndex(closeTabIndex);
      }
      this.props.dispatchCloseTab(closeTabIndex);
      const closeTabId = this.props.tabs.getIn([closeTabIndex, 'id']);
      if (closeTabId) {
        this.props.dispatchRemoveFlashContexts(['editor-code'], [`${prefixEditTabMsg}${closeTabId}`]);
      }
    }
  };

  handleModalCloseTab = () => {
    const closeTabIndex = this.state.closeTabIndex;
    const closeChildTabIndex = this.state.closeChildTabIndex;
    this.toggleCloseTabModal();
    if (closeChildTabIndex !== undefined) {
      this.handleCloseChild(closeChildTabIndex, closeTabIndex);
    } else {
      this.handleCloseParent(closeTabIndex);
    }
    this.setState({ closeTabIndex: undefined, closeChildTabIndex: undefined });
  };

  handleCancelModalCloseTab = () => {
    this.toggleCloseTabModal();
    this.setState({ closeTabIndex: undefined, closeChildTabIndex: undefined });
  };

  handleModalChangeCode = () => {
    const currentActiveTab = this.props.currentActiveTab;
    const childActiveTab = this.props.isMultiTab ? this.props.currentChildActiveTab : -1;
    this.props.dispatchGetExample(currentActiveTab, childActiveTab);
    const tabStatus =
      childActiveTab > -1
        ? this.props.tabsStatus.getIn([currentActiveTab, 'childTabs', childActiveTab, 'currentStatus'])
        : this.props.tabsStatus.getIn([currentActiveTab, 'parentTab', 'currentStatus']);
    // @ts-expect-error thunk dispatch argument count mismatch
    this.props.dispatchChangeSimulationStatus(currentActiveTab, tabStatus, childActiveTab);
    this.handleCancelModalChangeCode();
  };

  handleCancelModalChangeCode = () => {
    this.setState(previousState => ({ ...previousState, showChangeCodeModal: false }));
  };

  handleRemoveAlphaRecordsetQueries = (alphaId: string) => {
    queryClient.removeQueries(['alpha', alphaId, 'recordset']);
  };

  handleRemoveTabQueries = (tabIndex: number, childTabIndex?: number) => {
    const childTabPath = typeof childTabIndex !== 'undefined' ? ['children', childTabIndex] : [];
    const alphaIdPath = [tabIndex, ...childTabPath, 'alphaDetail', 'id'];

    const alphaId: string | undefined = this.props.simulationAlphas.getIn(alphaIdPath);
    if (alphaId) {
      this.handleRemoveAlphaRecordsetQueries(alphaId);
    }
  };

  handleCloseTab = (closeTabIndex: number) => {
    analytics.trackCustomEvent('sim_close_tab');
    const currentId = this.props.tabs.getIn([closeTabIndex, 'id']);
    const invalidId = this.props.invalidIds.find((id: string) => id === currentId);
    const showModal = !this.checkIfTabIsEmptyCode(closeTabIndex);
    if (showModal && invalidId) {
      this.setState({ closeTabIndex: closeTabIndex, showCloseTabModal: showModal });
    } else {
      this.handleRemoveTabQueries(closeTabIndex);
      this.handleCloseParent(closeTabIndex);
    }
  };

  handleCloseChildTab = (closeTabIndex: number, parentTabIndex: number = this.props.currentActiveTab) => {
    analytics.trackCustomEvent('sim_close_tab');
    const progressTab = this.props.progress.get(parentTabIndex);
    const showModal = !this.checkIfTabIsEmptyCode(parentTabIndex, closeTabIndex) && !this.props.data;
    const currentId = this.props.tabs.getIn([parentTabIndex, 'childTabs', closeTabIndex, 'id']);
    const invalidId = this.props.invalidIds.find((id: string) => id === currentId);
    if (showModal && invalidId) {
      this.setState({ closeTabIndex: parentTabIndex, closeChildTabIndex: closeTabIndex, showCloseTabModal: showModal });
    } else if (
      this.props.hasUpdatedProperties === true &&
      this.state.showPropertiesTabModal === false &&
      closeTabIndex === this.props.currentChildActiveTab
    ) {
      this.setState({ showPropertiesTabModal: () => this.handleCloseChildTab(closeTabIndex) });
    } else {
      this.handleRemoveTabQueries(parentTabIndex, closeTabIndex);

      if (progressTab.get('fetchingPercentage') || progressTab.get('fetchingPost') || progressTab.get('fetchingCancel')) {
        this.handlerCancelSimulation();
        this.handleCloseChild(closeTabIndex, parentTabIndex);
        // @ts-expect-error ImmutableJS getIn returns unknown
      } else if (this.props.tabs.getIn([parentTabIndex, 'childTabs']).size === 2) {
        if (progressTab.get('fetchingPercentage') || progressTab.get('fetchingPost') || progressTab.get('fetchingCancel')) {
          this.handlerCancelSimulation();
        }

        // In case the current simulation is multi-simulation, and number of children is two,
        // the rest tab will be changed to regular simulation, BUT alphaDetail (simulation data) will not be copied (actually it should be)
        // Because I do not want to mess with CHANGE_TO_SIMULATION code now, I also remove queries belong to the rest tab.
        // **Note**: Because there are two elements, so index will be 0 or 1, then I use XOR to toggle bit.
        const theRestTabIndex = closeTabIndex ^ 1;
        this.handleRemoveTabQueries(parentTabIndex, theRestTabIndex);

        this.props.dispatchChangeToSimulation(parentTabIndex, closeTabIndex);
      } else {
        this.handleCloseChild(closeTabIndex, parentTabIndex);
      }
    }
  };

  handleChangeVisualization = () => {
    const isVisualization = this.props.activeTab && this.props.activeTab.get('visualization');
    analytics.trackCustomEvent('sim_click_visualization', { event_label: isVisualization ? 'off' : 'on' });
    this.props.dispatchResetProperties();
    this.props.dispatchTogglevisualization(this.props.currentActiveTab);
  };

  isTabCodeEmpty = () => {
    const tabActive = this.props.tabs.get(this.props.currentActiveTab);
    if (tabActive === undefined) {
      return true;
    }
    const childTabs = tabActive.get('childTabs');
    if (tabActive.has('childTabs')) {
      return childTabs.filter((tab: EditorTabType) => tab.get('code').trim().length === 0).size !== 0;
    } else if (tabActive.get('superAlpha')) {
      return (
        tabActive.get('combo') === undefined ||
        tabActive.get('combo').trim().length === 0 ||
        tabActive.get('selection') === undefined ||
        tabActive.get('selection').trim().length === 0
      );
    } else {
      return tabActive.get('code') === undefined || tabActive.get('code').trim().length === 0;
    }
  };

  toggleCloseTabModal = () => {
    this.setState(previousState => ({ ...previousState, showCloseTabModal: false }));
  };

  getData(type: string, tabActive: EditorTabType, visualizationActive: boolean) {
    const childTabs = tabActive.get('childTabs');
    if (tabActive.has('childTabs')) {
      return childTabs
        .map((tab: EditorTabType) => getSettingsToMultiSimulate(type, this.props.options, tabActive, tab, visualizationActive))
        .toJS();
    } else {
      return getSettingsToSimulate(type, this.props.options, tabActive, visualizationActive);
    }
  }

  handlerCancelSimulation = () => {
    const index = this.props.currentActiveTab;
    const tabId = this.props.tabs.getIn([index, 'id']);
    const progressTab = this.props.progress.find((tab: any) => tab.get('id') === tabId).get('simulationId');
    // @ts-expect-error dispatch thunk argument type mismatch
    this.props.dispatchCancelSimulation(progressTab, tabId);
  };

  handlerCancelSimulationWithIndex = (index = this.props.currentActiveTab) => {
    const tabId = this.props.tabs.getIn([index, 'id']);
    const progressTab = this.props.progress.find((tab: any) => tab.get('id') === tabId)?.get('simulationId');
    if (progressTab) {
      // @ts-expect-error dispatch thunk argument type mismatch
      this.props.dispatchCancelSimulation(progressTab, tabId);
    } else {
      // log additional data for error
      const formattedProps = Object.entries(this.props).map(([key, value]) => [key, value?.toJS?.() ?? value]);
      Sentry.withScope(scope => {
        scope.setExtras({
          props: JSON.stringify(formattedProps),
          cancelSimulationIndex: index,
        });
        Sentry.captureException(new Error('progressTab is not found'));
      });
    }
  };

  handleCloneAlpha = () => {
    analytics.trackCustomEvent('sim_click_clone_alpha');
    const childActiveTab = this.props.isMultiTab ? this.props.currentChildActiveTab : null;
    const type = this.props.activeTab.get('superAlpha') ? 'superCode' : 'code';
    this.props.dispatchCloneAlphaInNewTab(this.props.currentActiveTab, childActiveTab, v1(), [], undefined, type);
  };

  handleSimulate = () => {
    this.handleSimulateTab();
  };

  handleSimulateTab = (activeIndexTab = this.props.currentActiveTab) => {
    const type = this.props.activeTab.get('superAlpha') ? 'SUPER' : 'REGULAR';
    if (isdeviceUpToTablet()) {
      const codeType = this.props.simulationAlphas.getIn([this.props.currentActiveTab, 'superAlpha']) ? 'superCode' : 'code';
      this.props.dispatchToggleSimulationFilterMobile(activeIndexTab, codeType, this.props.history.replace);
    }
    this.props.dispatchResetProperties();
    const { maxTruncation = 1, minTruncation = 0 } = this.props;
    const errors = checkSettings(this.props.tabs.get(activeIndexTab), [minTruncation, maxTruncation], type);

    if (errors.size > 0) {
      // @ts-expect-error dispatch thunk argument type mismatch
      this.props.dispatchFlashMessageError(Immutable.fromJS(errors));
    } else if (type === 'SUPER') {
      const visualization = this.props.activeTab && this.props.activeTab.get('visualization') ? true : false;
      this.props.dispatchSimulationPost({
        idParent: this.props.tabs.get(activeIndexTab).get('id'),
        indexChild: null,
        data: this.getData(type, this.props.tabs.get(activeIndexTab), visualization),
        visualization,
        history: this.props.history,
        currentActiveTab: this.props.currentActiveTab,
      });
    } else {
      const childActiveTab = this.props.isMultiTab ? this.props.currentChildActiveTab : null;
      const childTabs = this.props.tabs.get(activeIndexTab).get('childTabs');
      const visualization = this.props.activeTab && this.props.activeTab.get('visualization') ? true : false;
      this.props.dispatchSimulationPost({
        idParent: this.props.tabs.get(activeIndexTab).get('id'),
        indexChild: childTabs ? childActiveTab : null,
        data: this.getData(type, this.props.tabs.get(activeIndexTab), visualization),
        visualization,
        history: this.props.history,
        currentActiveTab: this.props.currentActiveTab,
      });
    }
  };

  handleScrollToChecks = (elem: string) => {
    const sectionToScroll: any = document.getElementById(`alphas-${elem}`);
    const elemScroll: any = document.getElementById('alphas-details-sections');
    if (sectionToScroll && elemScroll) {
      animateScrollTo(sectionToScroll.offsetTop, { element: elemScroll });
    }
  };

  handleCheckSubmission = () => {
    if (this.props.isFetchingProperties) {
      this.props.dispatchToggleFunctionsProperty('shouldCheck', true);
    } else {
      const hasErrors = validateFields(
        this.props.data,
        this.props.dispatchSetAlphaPropertiesData,
        this.props.dispatchSetAlphaPropertiesError
      );
      if (!hasErrors) {
        this.props.dispatchToggleFunctionsProperty('shouldCheck', false);
        this.handleScrollToChecks('testingStatus');
        const alphaId = this.props.data.get('id');
        const childId = this.props.isMultiTab ? this.props.activeTab.getIn(['childTabs', this.props.currentChildActiveTab, 'id']) : null;
        this.props.dispatchSimulationCheck(alphaId, this.props.activeTab.get('id'), childId);
      } else {
        this.handleScrollToChecks('properties');
      }
    }
  };

  handleSubmitAlpha = () => {
    if (this.props.isFetchingProperties) {
      this.props.dispatchToggleFunctionsProperty('shouldSubmit', true);
    } else {
      this.props.dispatchToggleFunctionsProperty('shouldSubmit', false);
      const hasErrors = validateFields(
        this.props.data,
        this.props.dispatchSetAlphaPropertiesData,
        this.props.dispatchSetAlphaPropertiesError
      );
      if (!hasErrors) {
        const visualization = this.props.activeTab && this.props.activeTab.get('visualization');
        const childId = this.props.isMultiTab ? this.props.activeTab.getIn(['childTabs', this.props.currentChildActiveTab, 'id']) : null;
        this.handleScrollToChecks('testingStatus');
        this.props.dispatchSubmitAlpha(this.props.alphaId, this.props.activeTab.get('id'), childId, visualization);
      } else {
        this.handleScrollToChecks('properties');
      }
    }
  };

  handleExample = () => {
    analytics.trackCustomEvent('click_example');

    const currentActiveTab = this.props.currentActiveTab;
    const childActiveTab = this.props.isMultiTab ? this.props.currentChildActiveTab : -1;
    let showModal = null;
    if (childActiveTab !== -1) {
      showModal = !this.checkIfTabIsEmptyCode(currentActiveTab, childActiveTab);
    } else {
      const tabActive = this.props.tabs.get(currentActiveTab);
      showModal = !(this.checkIfTabIsEmptyCode(currentActiveTab) || this.props.exampleArrayIds.includes(tabActive.get('id')));
    }

    if (showModal) {
      this.setState({ closeTabIndex: currentActiveTab, showChangeCodeModal: showModal });
    } else {
      this.props.dispatchGetExample(currentActiveTab, childActiveTab);
      const tabStatus =
        childActiveTab > -1
          ? this.props.tabsStatus.getIn([currentActiveTab, 'childTabs', childActiveTab, 'currentStatus'])
          : this.props.tabsStatus.getIn([currentActiveTab, 'parentTab', 'currentStatus']);
      // @ts-expect-error thunk dispatch argument count mismatch
      this.props.dispatchChangeSimulationStatus(currentActiveTab, tabStatus, childActiveTab);
    }
  };

  handleRenameTab = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    const length: number = e.target.value.length;
    e.target.style.width = `${(length + 1) * 8}px`;
    this.props.dispatchRenameTab(
      this.state.renameInfo.isChild ? this.props.currentActiveTab : this.state.renameInfo.index,
      this.state.renameInfo.isChild ? this.state.renameInfo.index : null,
      e.target.value
    );
  };

  handleDisableRename = (e: any) => {
    if (e.target.value.trim().length === 0) {
      this.props.dispatchRenameTab(
        this.state.renameInfo.isChild ? this.props.currentActiveTab : this.state.renameInfo.index,
        this.state.renameInfo.isChild ? this.state.renameInfo.index : null,
        this.state.renameInfo.name
      );
      // @ts-expect-error dispatch thunk argument type mismatch
      this.props.dispatchFlashMessageError(Immutable.fromJS(['Empty names are invalid']));
    }
    this.setState({
      canRename: false,
      renameInfo: null,
    });
  };

  handleEnableRename = ({ props }: ItemParams) => {
    this.setState({ renameInfo: props, canRename: true }, () => {
      const tabContainer = props.tabContainerElement as HTMLDivElement;
      const tabNameElement = tabContainer.querySelector<HTMLInputElement>('.editor-tabs__tab-text');
      tabNameElement?.focus();
      tabNameElement?.select();
    });
  };

  handleSimulateContextMenu = ({ props }: ItemParams) => {
    if (props.isChild) {
      this.handleSimulateTab();
    } else {
      this.handleSimulateTab(props.index);
    }
  };

  duplicateAlpha = ({ props }: ItemParams) => {
    const childTabs = this.props.tabs.getIn([props.index, 'childTabs']);
    if (props.isChild) {
      this.props.dispatchCloneAlphaInNewTab(this.props.currentActiveTab, props.index, v1(), []);
    } else if (childTabs) {
      // @ts-expect-error childTabs typed as any
      const idArray = childTabs.map((__: any) => v1());
      this.props.dispatchCloneAlphaInNewTab(props.index, null, v1(), idArray.toJS(), true);
    } else {
      const type = this.props.activeTab.get('superAlpha') ? 'superCode' : 'code';
      this.props.dispatchCloneAlphaInNewTab(props.index, null, v1(), [], undefined, type);
    }
  };

  changeColor = ({ props }: ItemParams) => {
    const payload = {
      color: props.color !== 'none' && props.color.trim().length !== 0 ? props.color : null,
    };
    const childTabs = this.props.tabs.getIn([props.index, 'childTabs']);
    if (props.isChild) {
      this.props.dispatchSaveAlphaProperties(
        this.props.simulationAlphas.getIn([this.props.currentActiveTab, 'children', props.index, 'alphaDetail', 'id']),
        payload,
        this.props.activeTab.get('id'),
        props.index
      );
    } else if (childTabs) {
      this.props.simulationAlphas.getIn([props.index, 'children']).map((childTab: any, index: number) =>
        this.props.dispatchSaveAlphaProperties(
          childTab.getIn(['alphaDetail', 'id']),
          payload,
          // @ts-expect-error ImmutableJS getIn returns unknown
          this.props.tabs.getIn([data.index, 'id']),
          index
        )
      );
    } else {
      this.props.dispatchSaveAlphaProperties(
        this.props.simulationAlphas.getIn([props.index, 'alphaDetail', 'id']),
        payload,
        // @ts-expect-error ImmutableJS getIn returns unknown
        this.props.tabs.getIn([data.index, 'id']),
        null
      );
    }
  };

  handlePropertiesSave = () => {
    const alphaId = this.props.data.get('id');

    if (this.props.currentActiveTab !== undefined) {
      const childActiveTab = this.props.isMultiTab ? this.props.currentChildActiveTab : null;
      this.props.dispatchSaveAlphaProperties(alphaId, undefined, this.props.activeTab.get('id'), childActiveTab);
    }
  };

  handleCancelCloseAlphaProperties = () => {
    this.setState({ showPropertiesTabModal: false });
  };

  handleSaveAlphaProperties = () => {
    this.props.dispatchResetProperties();
    this.handlePropertiesSave();
    const callback = this.state.showPropertiesTabModal();
    this.setState({ showPropertiesTabModal: false }, callback);
  };

  handleNotSaveCloseAlphaProperties = () => {
    this.props.dispatchResetProperties();
    const callback = this.state.showPropertiesTabModal;
    this.setState({ showPropertiesTabModal: false }, () => {
      callback();
    });
  };

  getPath = () => {
    if (this.props.activeTab) {
      if (this.props.currentActiveTab >= 0 && this.props.activeTab.get('activeChildTab') >= 0) {
        return ['simulation', 'data', this.props.currentActiveTab, 'children', this.props.activeTab.get('activeChildTab')];
      } else {
        return ['simulation', 'data', this.props.currentActiveTab];
      }
    }

    return null;
  };

  render() {
    const { permissions, tabs, currentChildActiveTab, currentMultiTabs, currentActiveTab, data, progress } = this.props;
    const language =
      tabs.getIn([currentActiveTab, 'settings', 'language']) !== undefined
        ? tabs.getIn([currentActiveTab, 'settings', 'language', 'value'])
        : tabs.getIn([currentActiveTab, 'settings', 'language', 'value']);

    return (
      <ErrorBoundary showUI={true} isNotFullScreen={true}>
        {this.props.fetchingOptions ? (
          <div className="loader loader--app">
            <Loader active={true} inline="centered" size="massive" />
          </div>
        ) : (
          // TODO: Add missing props to this component
          <EditorMain
            tabs={tabs}
            currentChildActiveTab={currentChildActiveTab}
            currentMultiTabs={currentMultiTabs}
            handleCloseTab={this.handleCloseTab}
            currentActiveTab={currentActiveTab}
            handleCloseChildTab={this.handleCloseChildTab}
            handleCreateNewMultiTab={this.handleCreateNewMultiTab}
            handleCreateNewTab={this.handleCreateNewTab}
            handleCreateNewChildTab={this.handleCreateNewChildTab}
            handleSetChildTabAsActive={this.handleSetChildTabAsActive}
            handleSetTabAsActive={this.handleSetTabAsActive}
            handleSimulate={this.handleSimulate}
            handleExample={this.handleExample}
            onCodeChange={this.onCodeChange}
            permissions={permissions}
            handleCheckSubmission={this.handleCheckSubmission}
            data={data}
            handleCloneAlpha={this.handleCloneAlpha}
            progress={progress}
            handlerCancelSimulation={this.handlerCancelSimulation}
            handleChangeVisualization={this.handleChangeVisualization}
            isPythonCode={language === 'PYTHON' ? true : false}
            handleSubmitAlpha={this.handleSubmitAlpha}
            // TODO: getPath should return string[] instead of `any[] | null`
            // @ts-expect-error Router child path prop typing
            path={this.getPath()}
            emptyCode={this.isTabCodeEmpty()}
            userId={this.props.userId}
            handleRenameTab={this.handleRenameTab}
            handleDisableRename={this.handleDisableRename}
            canRename={this.state.canRename}
            renameInfo={this.state.renameInfo}
            handleEnableRename={this.handleEnableRename}
            handleSimulateContextMenu={this.handleSimulateContextMenu}
            duplicateAlpha={this.duplicateAlpha}
            changeColor={this.changeColor}
            simulationAlphas={this.props.simulationAlphas}
            showCloseTabModal={this.state.showCloseTabModal}
            handleCancelCloseAlphaProperties={this.handleCancelCloseAlphaProperties}
            handleSaveAlphaProperties={this.handleSaveAlphaProperties}
            handleNotSaveCloseAlphaProperties={this.handleNotSaveCloseAlphaProperties}
            showChangeCodeModal={this.state.showChangeCodeModal}
            toggleCloseTabModal={this.toggleCloseTabModal}
            handleCancelModalCloseTab={this.handleCancelModalCloseTab}
            handleModalCloseTab={this.handleModalCloseTab}
            handleCancelModalChangeCode={this.handleCancelModalChangeCode}
            handleModalChangeCode={this.handleModalChangeCode}
            userSettings={this.props.userSettings}
            showPropertiesTabModal={this.state.showPropertiesTabModal}
            handleShowPropertiesTabModalState={this.handleShowPropertiesTabModalState}
            checkIfTabIsEmptyCode={this.checkIfTabIsEmptyCode}
            handleNewSuperAlpha={this.handleNewSuperAlpha}
            tabsStatus={this.props.tabsStatus}
            changeSimulationStatus={this.props.dispatchChangeSimulationStatus}
            handleChangeTabPosition={this.props.dispatchChangeTabPosition}
            handleChangeTabPositionChild={this.props.dispatchChangeTabPositionChild}
            dispatchChangingTabPosition={this.props.dispatchChangingTabPosition}
            dispatchStopChangingTabPosition={this.props.dispatchStopChangingTabPosition}
          />
        )}
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = (state: StateType): EditorStateProps => ({
  // @ts-expect-error connect own-props collapse under @types/react@19
  tabs: getTabsSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  userId: getUserIdSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  isFetching: alphasFetchingDataSelector(state),
  isFetchingCheck: dataFetchingCheckSelector(state),
  currentActiveTab: activeTabIndexSelector(state),
  currentMultiTabs: childTabsSelector(state) || Immutable.List(),
  currentChildActiveTab: currentChildActiveTabLessOne(state),
  options: editorOptionsSelector(state),
  permissions: getUserPermissionsSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  fetchingOptions: editorFetchingSettingsSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  filters: filtersSelector(state),
  fetchingPercentage: fetchingPercentageSelector(state),
  fetchingPost: fetchingPostSelector(state),
  fetchingSelectionCode: fetchingSelectionCodeSelector(state),
  data: alphaDetailSimulationSelector(state),
  userSettings: getUserSettingsSelector(state),
  progress: progressSelector(state),
  isMultiTab: childTabsSelector(state) ? true : false,
  isFetchingProperties: isFetchingPropertiesSimulationSelector(state),
  alphaId: alphaDetailIdSimulationSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  maxTruncation: maxTruncationSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  minTruncation: minTruncationSelector(state),
  activeTab: activeTabSelector(state),
  simulationAlphas: dataSelector(state),
  invalidIds: getInvalidIdsSelector(state),
  exampleArrayIds: getExampleArrayIdsSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  hasUpdatedProperties: getHasUpdatedPropertiesSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  shouldCheck: shouldCheckSelector(state),
  // @ts-expect-error connect own-props collapse under @types/react@19
  shouldSubmit: shouldSubmitSelector(state),
  tabsStatus: tabsStatusSelector(state),
});

interface DispatchSimulationPostParams {
  idParent: string;
  indexChild: any;
  data: any;
  visualization: boolean;
  history: History;
  currentActiveTab: any;
}

function mapDispatchToProps(dispatch: Dispatch<any>): EditorStateDispatchers {
  return {
    dispatchCreateNewTab: (data: any) => dispatch(createNewTab(data)),
    dispatchCreateNewSuperAlphaTab: (data: any) => dispatch(createNewSuperAlphaTab(data)),
    dispatchCreateNewChildTab: (index: number, data: any) => dispatch(createNewChildTab(index, data)),
    dispatchCreateNewMultiTab: (data: any) => dispatch(createNewMultiTab(data)),
    dispatchSetActiveTab: (activeTabIndex: number, history: History<unknown>) => dispatch(setActiveEditorTab(activeTabIndex, history)),
    dispatchSetChildTabAsActive: (activeTabIndex: number, parentTabIndex: number) =>
      dispatch(setActiveChildTab(activeTabIndex, parentTabIndex)),
    dispatchCloseTab: (closeTabIndex: number) => dispatch(closeTab(closeTabIndex)),
    dispatchCloseChildTab: (closeTabIndex: number, closeChildTabIndex: number) =>
      dispatch(closeChildTab(closeTabIndex, closeChildTabIndex)),
    dispatchChangeToSimulation: (closeTabIndex: number, closeChildTabIndex: number) =>
      dispatch(changeToSimulation(closeTabIndex, closeChildTabIndex)),
    dispatchUpdateTabCode: (newValue: any, codeType: string, currentTabIndex: number, childActiveTab: number) =>
      dispatch(setTabCode(newValue, codeType, currentTabIndex, childActiveTab)),
    dispatchSimulationOptions: () => dispatch(fetchSimulationOptions()),
    dispatchSimulationPost: ({ idParent, indexChild, data, visualization, history, currentActiveTab }: DispatchSimulationPostParams) =>
      dispatch(
        fetchSimulationPost({
          id: idParent,
          indexChild: indexChild,
          data: data,
          isVisualization: visualization,
          history: history,
          currentActiveTab: currentActiveTab,
        })
      ),
    dispatchFlashMessageError: (message: Immutable.List<string>) =>
      dispatch(addFlashMessages(message, { context: 'generic', intl: false })),
    dispatchCancelSimulation: async (simulationId: string, id: string) => dispatch(cancelSimulation(simulationId, id)),
    dispatchSaveAlphaProperties: async (alphaId: string, data: any, idParent: string, indexChild: any) =>
      dispatch(updateAlphaPropertiesSimulation(alphaId, data, idParent, indexChild)),
    dispatchSimulationCheck: async (alphaId: string, idParent: string, childId: string | null) =>
      dispatch(simulationCheck(alphaId, idParent, childId)),
    dispatchGetExample: async (currentTabIndex: number, childActiveTab: number) => dispatch(getExample(currentTabIndex, childActiveTab)),
    dispatchCloneAlphaFromContent: (alphaDetail: any) => dispatch(cloneAlphaFromContent(alphaDetail)),
    dispatchCloneAlphaInNewTab: (
      activeTab: number,
      childActiveTab: number | null,
      idParent: any,
      idChild: any[],
      multi?: boolean,
      type?: string
    ) => {
      dispatch(cloneAlpha(activeTab, childActiveTab, idParent, idChild, multi, type));
    },
    dispatchSubmitAlpha: async (alphaId: string, idParent: string, childId: string | null, isVisualization: boolean) =>
      dispatch(submitAlphaSimulation(alphaId, idParent, childId, isVisualization)),
    dispatchRenameTab: (tabIndex: number, indexChild: any, newName: string) => dispatch(renameTab(tabIndex, indexChild, newName)),
    dispatchUpdateFiltersForSingleFilter: historyReplace => dispatch(updateFiltersForSingleFilterThunk(historyReplace)),
    dispatchTogglevisualization: (tabIndex: number) => dispatch(toggleVisualization(tabIndex)),
    dispatchAddActiveContexts: (contexts: string[]) => dispatch(addActiveContexts(contexts)),
    dispatchChangeActiveContexts: (contexts: string[]) => dispatch(changeActiveContexts(contexts)),
    dispatchResetProperties: () => dispatch(resetProperties()),
    dispatchClearAllFlashContexts: () => dispatch(clearAllFlashContexts()),
    dispatchOnlyGenericFlashMessages: () => dispatch(onlyGenericFlashMessages()),
    dispatchRemoveFlashContexts: (path: string[], contexts: string[]) => dispatch(removeFlashContexts(path, contexts)),
    dispatchToggleSimulationFilterMobile: (tabIndex: number, codeType: string, historyReplace) =>
      dispatch(toggleSimulationFilterMobileThunk(tabIndex, codeType, historyReplace)),
    dispatchToggleFunctionsProperty: (property: string, value: boolean) => dispatch(toggleFunctionsProperty(property, value)),
    dispatchSetAlphaPropertiesError: (hasError: boolean) => dispatch(setAlphaPropertiesError(hasError)),
    dispatchSetAlphaPropertiesData: (data: any) => dispatch(setAlphaPropertiesData(data)),
    dispatchChangeSimulationStatus: (tabIndex: number, currentStatus: string, childIndex?: number, nextStatus?: string) =>
      dispatch(changeSimulationStatus(tabIndex, currentStatus, childIndex, nextStatus)),
    dispatchChangeTabPosition: (source: number, destination: number) => dispatch(changeTabPosition({ source, destination })),
    dispatchChangeTabPositionChild: (currentActiveTab: number, source: number, destination: number) =>
      dispatch(changeTabPositionChild({ currentActiveTab, source, destination })),
    dispatchSetDefaultFilters: (filters: Object, tabIndex: number) => dispatch(setDefaultFilters(filters, tabIndex)),
    dispatchChangingTabPosition: () => dispatch(changingTabPosition()),
    dispatchStopChangingTabPosition: () => dispatch(stopChangingTabPosition()),
    dispatchResetFilters: () => dispatch(resetFilters()),
  };
}

export default withRouter(
  connect<EditorStateProps, DispatchConnect<EditorStateDispatchers>, any, StateType>(mapStateToProps, mapDispatchToProps, undefined, {
    forwardRef: true,
  })(Editor)
);
